import { prefersReducedMotion } from '@theme/utilities';

/**
 * Scroll Animation System
 * Provides smooth reveal animations for theme elements
 */

class ScrollAnimations {
  constructor() {
    this.observer = null;
    this.animatedElements = new Set();
    this.settings = this.getSettings();
    this.init();
  }

  getSettings() {
    // Get theme settings from global Shopify object or defaults
    const themeSettings = window.Shopify?.theme?.settings || {};
    return {
      enabled: themeSettings.scroll_animations_enabled !== false,
      duration: themeSettings.scroll_animation_duration || 'normal',
      defaultType: themeSettings.scroll_animation_type || 'fade-in'
    };
  }

  init() {
    // Skip animations if disabled in theme settings or user prefers reduced motion
    if (!this.settings.enabled || prefersReducedMotion()) {
      return;
    }

    this.setupIntersectionObserver();
    this.observeElements();
  }

  setupIntersectionObserver() {
    const options = {
      root: null,
      rootMargin: '0px 0px -10% 0px', // Trigger when element is 10% from bottom of viewport
      threshold: 0.1
    };

    this.observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          this.animateElement(entry.target);
        }
      });
    }, options);
  }

  observeElements() {
    // Find all elements with animation classes
    const selectors = [
      '[data-scroll-animate]',
      '.scroll-fade-in',
      '.scroll-slide-up',
      '.scroll-slide-left',
      '.scroll-slide-right',
      '.scroll-scale-up',
      '.scroll-stagger'
    ];

    selectors.forEach(selector => {
      const elements = document.querySelectorAll(selector);
      elements.forEach(element => {
        this.addElement(element);
      });
    });

    // Also observe dynamic content
    this.observeDynamicContent();
  }

  addElement(element) {
    if (this.animatedElements.has(element)) {
      return;
    }

    // Set initial state
    this.setInitialState(element);
    
    // Add to observer
    this.observer?.observe(element);
    this.animatedElements.add(element);
  }

  setInitialState(element) {
    const animationType = element.dataset.scrollAnimate || this.getAnimationType(element);
    
    // Add base animation class
    element.classList.add('scroll-animation-element');
    
    // Set initial transform based on animation type
    switch (animationType) {
      case 'fade-in':
        element.classList.add('scroll-fade-in');
        break;
      case 'slide-up':
        element.classList.add('scroll-slide-up');
        break;
      case 'slide-left':
        element.classList.add('scroll-slide-left');
        break;
      case 'slide-right':
        element.classList.add('scroll-slide-right');
        break;
      case 'scale-up':
        element.classList.add('scroll-scale-up');
        break;
      case 'stagger':
        element.classList.add('scroll-stagger');
        this.setupStaggerAnimation(element);
        break;
      default:
        element.classList.add('scroll-fade-in');
    }
  }

  getAnimationType(element) {
    // Determine animation type from class names
    if (element.classList.contains('scroll-fade-in')) return 'fade-in';
    if (element.classList.contains('scroll-slide-up')) return 'slide-up';
    if (element.classList.contains('scroll-slide-left')) return 'slide-left';
    if (element.classList.contains('scroll-slide-right')) return 'slide-right';
    if (element.classList.contains('scroll-scale-up')) return 'scale-up';
    if (element.classList.contains('scroll-stagger')) return 'stagger';
    return 'fade-in';
  }

  setupStaggerAnimation(container) {
    const children = container.children;
    Array.from(children).forEach((child, index) => {
      child.style.setProperty('--stagger-delay', `${index * 100}ms`);
      child.classList.add('scroll-fade-in');
    });
  }

  animateElement(element) {
    element.classList.add('scroll-animated');
    
    // Remove from observer after animation
    this.observer?.unobserve(element);
  }

  observeDynamicContent() {
    // Observe for dynamically added content (e.g., product cards, search results)
    const contentObserver = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        mutation.addedNodes.forEach((node) => {
          if (node.nodeType === Node.ELEMENT_NODE) {
            // Check if the node itself has animation classes
            this.checkAndAddElement(node);
            
            // Check children for animation classes
            const animatedChildren = node.querySelectorAll(
              '[data-scroll-animate], .scroll-fade-in, .scroll-slide-up, .scroll-slide-left, .scroll-slide-right, .scroll-scale-up, .scroll-stagger'
            );
            animatedChildren.forEach(child => this.checkAndAddElement(child));
          }
        });
      });
    });

    contentObserver.observe(document.body, {
      childList: true,
      subtree: true
    });
  }

  checkAndAddElement(element) {
    const hasAnimationClass = element.matches(
      '[data-scroll-animate], .scroll-fade-in, .scroll-slide-up, .scroll-slide-left, .scroll-slide-right, .scroll-scale-up, .scroll-stagger'
    );

    if (hasAnimationClass && !this.animatedElements.has(element)) {
      this.addElement(element);
    }
  }

  // Public method to manually trigger animations
  animateElementNow(element) {
    if (element && !element.classList.contains('scroll-animated')) {
      this.animateElement(element);
    }
  }

  // Public method to refresh observer (useful for dynamic content)
  refresh() {
    this.observeElements();
  }

  destroy() {
    this.observer?.disconnect();
    this.animatedElements.clear();
  }
}

// Initialize scroll animations
const scrollAnimations = new ScrollAnimations();

// Export for use in other modules
export default scrollAnimations;

// Auto-initialize when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    scrollAnimations.refresh();
  });
} else {
  scrollAnimations.refresh();
}

// Re-initialize on page transitions (for SPA-like navigation)
document.addEventListener('shopify:section:load', () => {
  scrollAnimations.refresh();
});

// Handle theme editor changes
if (window.Shopify && window.Shopify.designMode) {
  document.addEventListener('shopify:section:load', () => {
    scrollAnimations.refresh();
  });
}
